import { createSelector } from 'reselect';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import zipObject from 'lodash/zipObject';
import { metricKeys } from '@pulse/metrics';
import { DATASET, INTERNAL_UNKNOWN, CURRENT_PRODUCT_MODE } from '@utils/constants';
import { selector as commonSelector } from '@store/common';
import featureNames from '@utils/featureToggle/constants';
import { INVERTED_METRIC_SWITCH_MAP, BASE_METRICS } from '@utils/metrics';
import { combineWithMetricDeDuplication } from '@utils/metrics/metricHelpers';
import { SCOPE } from './types';
import { trendsViewSelector } from '../base/selectors';
import {
  DEFAULT_NOC_METRIC_SORT,
  DEFAULT_NOC_METRICS,
} from '@components/noc-metric-panel/utils/constants';

const selector = (state) => get(state, SCOPE);

// TODO: can be removed when cleaning threshold apis and responses
const realtimeTrendsThresholdRequestBody = {
  queries: [
    {
      type: 'groupby',
      dataset: DATASET,
      metrics: [metricKeys.Plays, metricKeys.Attempts],
      filter: { id: 123 },
      limit: 200,
      groupBy: ['m3.dv.n'],
      options: {
        withTotals: true,
      },
    },
  ],
};

const realtimeTrendsRequestBody = {
  EI: {
    type: 'groupby',
    dataset: DATASET,
    metrics: [DEFAULT_NOC_METRIC_SORT.EI.metric],
    filter: {},
    limit: 200,
    groupBy: [],
    granularity: 'ALL',
    interval: '',
    sortBy: [DEFAULT_NOC_METRIC_SORT.EI],
    options: { withTotals: true },
  },
  AI: {
    type: 'groupby',
    dataset: DATASET,
    metrics: [DEFAULT_NOC_METRIC_SORT.AI.metric],
    filter: {},
    limit: 200,
    groupBy: [],
    granularity: 'ALL',
    interval: '',
    sortBy: [DEFAULT_NOC_METRIC_SORT.AI],
    options: { withTotals: true },
  },
  APP: {
    type: 'group-by',
    dataset: DATASET,
    metrics: [DEFAULT_NOC_METRIC_SORT.APP.metric],
    filter: {},
    limit: 200,
    groupBy: [],
    granularity: 'ALL',
    interval: '',
    sortBy: [DEFAULT_NOC_METRIC_SORT.APP],
    isLiveMode: true,
    options: { withTotals: true },
  },
};

export const filterSelector = createSelector(
  trendsViewSelector,
  (trendsState) => trendsState.appliedFilter[0],
);

export const dimensionSelector = createSelector(
  trendsViewSelector,
  (trendsState) => trendsState.dimension,
);

// returns inverted metrics
// used in apis, table
export const metricSelector: any = createSelector(trendsViewSelector, (trendsState) =>
  trendsState.metrics.map((metric) => get(INVERTED_METRIC_SWITCH_MAP, metric, metric)),
);

// raw metrics as in store (for select fields)
export const rawMetricSelector = createSelector(
  trendsViewSelector,
  (trendsState) => trendsState.metrics,
);

// TODO: can be removed when cleaning threshold apis and responses
export const trendsThresholdPayloadSelector = createSelector(
  metricSelector,
  filterSelector,
  dimensionSelector,
  commonSelector,
  (metrics, filter, dimension, commonStore) => {
    const body = cloneDeep(realtimeTrendsThresholdRequestBody);
    // always include baseline metrics
    body.queries[0].limit = commonStore.features[featureNames.REALTIME_ROWS_PER_DIMENSION];
    body.queries[0].metrics = combineWithMetricDeDuplication(metrics, BASE_METRICS);
    body.queries[0].filter = filter;
    body.queries[0].groupBy = [dimension];
    return body;
  },
);

export const payloadSelector = createSelector(
  metricSelector,
  filterSelector,
  dimensionSelector,
  commonSelector,
  (metrics, filter, dimension, commonStore) => {
    const invertedMetrics = [];
    const metricsList = combineWithMetricDeDuplication(metrics, BASE_METRICS);
    metricsList.forEach((m) => {
      if (m in INVERTED_METRIC_SWITCH_MAP) {
        invertedMetrics.push(INVERTED_METRIC_SWITCH_MAP[m]);
      }
    });
    const query = cloneDeep(realtimeTrendsRequestBody[CURRENT_PRODUCT_MODE]);
    query.limit = commonStore.features[featureNames.REALTIME_ROWS_PER_DIMENSION];
    query.metrics = combineWithMetricDeDuplication(DEFAULT_NOC_METRICS[CURRENT_PRODUCT_MODE], [
      ...metricsList,
      ...invertedMetrics,
    ]); // to remove duplicates
    query.filter = filter.rule;
    query.groupBy = [dimension];

    return {
      queries: [query],
    };
  },
);

export const dataSelector = createSelector(selector, (trendsState) =>
  trendsState.response ? trendsState.response[0] : {},
);

export const loadingSelector = createSelector(selector, (trendsState) => ({
  loading: trendsState.isFetching,
  errorCode: trendsState.errorCode,
  errorMsg: trendsState.errorMsg,
}));

export const trendsTableDataSortSelector = createSelector(
  trendsViewSelector,
  dataSelector,
  payloadSelector,
  (trendsData, tableData, payload) => {
    const { sort } = trendsData;
    let unknownRow = null;
    const records = [];
    const metrics = get(payload, ['queries', 0, 'metrics'], []);
    const maxData = zipObject(metrics, Array.from({ length: metrics.length }).fill(0)) as Record<
      string,
      number
    >;

    const total = { name: ['Total'], ...get(tableData, 'totals[0].results', {}), isTotalRow: true };

    get(tableData, 'data[0].results', []).forEach((row) => {
      const value = row.value.join('');
      // build row data
      const rowData = {
        id: value,
        ...row,
      };
      // for App to sort
      metrics.forEach((metric) => {
        const value = rowData[metric] === 'NA' ? 0 : rowData[metric];
        if (value > maxData[metric]) {
          maxData[metric] = value;
        }
      });
      // Unknown row
      if (value !== INTERNAL_UNKNOWN) {
        records.push(rowData);
      } else {
        unknownRow = rowData;
      }
    });

    // sort data
    const sortedData = orderBy(
      records,
      (v) => (v[sort.key] === 'NA' ? 0 : v[sort.key]),
      sort.order,
    );
    if (unknownRow) {
      sortedData.push(unknownRow);
    }
    return {
      data: sortedData,
      total,
      sort,
      maxData,
    };
  },
);

export default selector;
